import api from './api';
import { User } from '../types';

export const usersService = {
  // Get all users (ADMIN / CO_ADMIN only)
  async getAll(): Promise<User[]> {
    const response = await api.get('/users');
    return response.data;
  },

  // Get leaderboard — limited public fields, accessible to all authenticated users
  async getLeaderboard(): Promise<Pick<User, 'id' | 'fullName' | 'profilePicture' | 'department' | 'branch'>[]> {
    const response = await api.get('/users/leaderboard');
    return response.data;
  },

  /** Branch names from user records (polls, forms). */
  async getBranches(): Promise<string[]> {
    const response = await api.get<string[]>('/users/branches');
    return response.data;
  },

  /** Department names; pass branch to limit to that branch. */
  async getDepartments(branch?: string): Promise<string[]> {
    const response = await api.get<string[]>('/users/departments', {
      params: branch?.trim() ? { branch: branch.trim() } : undefined,
    });
    return response.data;
  },

  // Get single user
  async getById(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Create new user
  async create(data: Partial<User> & { password: string }): Promise<User> {
    const response = await api.post('/users', data);
    return response.data;
  },

  // Update user (profile fields only — not `role`; use changeRole for that)
  async update(
    id: string,
    data: Omit<Partial<User>, 'role' | 'id' | 'createdAt' | 'updatedAt' | 'profilePicture'> & {
      password?: string;
    },
  ): Promise<User> {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  /** ADMIN only — PATCH /users/:id/role */
  async changeRole(id: string, role: User['role']): Promise<User> {
    const response = await api.patch(`/users/${id}/role`, { role });
    return response.data;
  },

  // Delete user
  async delete(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/users/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  // Download CSV import template
  async downloadImportTemplate(): Promise<void> {
    const buildAndDownloadCsv = (rows: User[]) => {
      const escapeCsv = (value: string | null | undefined): string => {
        const raw = String(value ?? '');
        if (/[",\n\r]/.test(raw)) {
          return `"${raw.replace(/"/g, '""')}"`;
        }
        return raw;
      };

      const header =
        'Emp No,Full Name,Department,Branch,Password,Role,User Email,Designation,Mobile';
      const lines = rows.map((u) =>
        [
          escapeCsv(u.empNo),
          escapeCsv(u.fullName),
          escapeCsv(u.department),
          escapeCsv(u.branch),
          '',
          escapeCsv(u.role),
          escapeCsv(u.email),
          escapeCsv(u.designation),
          escapeCsv(u.telNo),
        ].join(','),
      );

      const blob = new Blob([[header, ...lines].join('\n')], {
        type: 'text/csv;charset=utf-8',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'user-import-template.csv';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    };

    try {
      const response = await api.get('/users/import-template', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'user-import-template.csv';
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const users = await this.getAll();
        buildAndDownloadCsv(users);
        return;
      }
      throw error;
    }
  },
};
